/**
 * kioskApi
 *
 * Simple explanation: Fetches weather/tide/lunar-solar data for kiosk slides.
 *
 * Technical summary:
 * - Combines multiple sources (NTI, ECCC, internal API)
 * - Skips requests for non-operational stations to save time
 * - Provides a plain function for the map and helpers for slides
 */
import axios from "axios";
import { stationMetaData } from "../../../utils/rawData";
import { fetchNTIData } from "../../../hooks/useWeatherData";
import { useQuery } from "react-query";
import { refetchIntervalFn } from "./kioskRefetch";

// --- API Endpoints and Constants ---
export const API_BASE_URL = "https://client-api.silanga.ca/aws";
export const ECCC_API_BASE_URL = "https://client-api.silanga.ca/ECCC";
export const API_KEY = process.env.REACT_APP_API_TOKEN;
export const NTI_API_BASE_URL = "https://www.smartatlantic.ca/erddap/tabledap";
export const TIDE_API_BASE_URL =
  "https://client-api.silanga.ca/tide-prediction";
const LUNAR_SOLAR_API_BASE_URL = "https://client-api.silanga.ca/lunar-solar";
// No need to import transformNTIData

/**
 * Fetch current weather for a station (handles NTI, ECCC, regular)
 */
export async function fetchCurrentWeather(stationId) {
  const station = stationMetaData.find((s) => s.id === stationId);
  if (!station) throw new Error("Station not found");

  // Skip API calls for non-operational stations
  if (station.status !== "Operational") {
    throw new Error("Station is not operational");
  }

  if (station.type === "NTI_Weather_Station") {
    const response = await fetchNTIData(stationId);
    return response[0];
  } else {
    const baseUrl =
      station.type === "ECCC_Weather_Station"
        ? ECCC_API_BASE_URL
        : API_BASE_URL;
    const response = await axios.get(baseUrl, {
      params: { uid: stationId },
      headers: { "x-api-key": API_KEY },
    });
    return response.data[0];
  }
}

/**
 * Fetch last week's weather for a station
 */
export async function fetchLastWeekWeather(stationId) {
  const station = stationMetaData.find((s) => s.id === stationId);
  if (!station) throw new Error("Station not found");

  // Skip API calls for non-operational stations
  if (station.status !== "Operational") {
    throw new Error("Station is not operational");
  }

  if (station.type === "NTI_Weather_Station") {
    return await fetchNTIData(stationId, true);
  } else {
    const baseUrl =
      station.type === "ECCC_Weather_Station"
        ? ECCC_API_BASE_URL
        : API_BASE_URL;
    const response = await axios.get(baseUrl, {
      params: { uid: stationId, last_week: true },
      headers: { "x-api-key": API_KEY },
    });
    return response.data;
  }
}

/**
 * Fetch all weather stations for kiosk map (no react-query, just a plain async function)
 * Returns an array of station data
 */
export async function fetchKioskWeatherStationsMap() {
  // Fetch all stations in parallel
  const results = await Promise.all(
    stationMetaData.map(async (station) => {
      try {
        // Skip API calls for non-operational stations
        if (station.status !== "Operational") {
          return {
            ...station,
            lat: station.default_latitude,
            lon: station.default_longitude,
            info: "Non - Operational",
            hasError: false,
          };
        }

        let data;
        if (station.type === "NTI_Weather_Station") {
          data = await fetchNTIData(station.id);
        } else {
          const baseUrl =
            station.type === "ECCC_Weather_Station"
              ? ECCC_API_BASE_URL
              : API_BASE_URL;
          const response = await axios.get(baseUrl, {
            params: { uid: station.id },
            headers: { "x-api-key": API_KEY },
          });
          data = response.data[0];
        }
        const temperature = data?.elements?.temperature_ext?.value;
        const qcValue = data?.elements?.temperature_ext?.qc_value;
        return {
          ...station,
          lat: station.default_latitude,
          lon: station.default_longitude,
          info:
            qcValue !== undefined
              ? qcValue !== 100
                ? " - °C"
                : `${temperature}°C`
              : temperature !== undefined
              ? `${temperature}°C`
              : "N/A",
          hasError: false,
        };
      } catch (error) {
        return {
          ...station,
          lat: station.default_latitude,
          lon: station.default_longitude,
          info: "Non - Operational",
          hasError: true,
        };
      }
    })
  );
  return results;
}

/**
 * Fetch tide prediction data for a station
 */
export async function fetchTidePrediction(stationId) {
  const station = stationMetaData.find((s) => s.id === stationId);
  if (!station) throw new Error("Station not found");

  // Get current UTC time minus 1 day to include past data
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000); // Subtract 24 hours
  const startTime = oneDayAgo.toISOString().slice(0, 19).replace("T", " ");

  const response = await axios.get(TIDE_API_BASE_URL, {
    params: {
      uid: stationId,
      start_time: startTime,
    },
    headers: { "x-api-key": API_KEY },
  });

  return response.data;
}

export async function fetchLunarSolarData(stationId) {
  const station = stationMetaData.find((s) => s.id === stationId);
  if (!station) throw new Error("Station not found");

  const response = await axios.get(LUNAR_SOLAR_API_BASE_URL, {
    params: {
      longitude: station.default_longitude,
      latitude: station.default_latitude,
    },
    headers: { "x-api-key": API_KEY },
  });

  return response.data;
}
